/**
 * The draft behind the process assignments' Save bar: `process → model_key`,
 * kept as picks that differ from what is stored. Save sends one assignment per
 * changed row, one after another. A row that saved is clean at once; a row
 * that failed stays dirty (and is listed with the server's reason, such as a
 * model of the wrong capability), so Save retries just those
 * and Reset puts just those back to their stored model.
 */

import { useState } from 'react';

import { useAssignProcess } from '@/api/queries';
import { toast } from '@/ui/Toast';
import type { AiProcess } from '@/api/types';
import type { ButtonState } from '@/ui/ActionButton';

export function useAssignmentsDraft(processes: AiProcess[]) {
  const assign = useAssignProcess({ quiet: true });
  const [picks, setPicks] = useState<Record<string, string>>({});
  // process key → the server's reason it refused that row.
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<{ error: unknown; at?: number }>({ error: null });

  const dirty = processes.filter((p) => picks[p.key] !== undefined && picks[p.key] !== p.model_key);
  const failures = dirty.flatMap((p) => (failed[p.key] === undefined ? [] : [{ name: p.name, message: failed[p.key]! }]));

  const valueOf = (process: AiProcess) => picks[process.key] ?? process.model_key ?? '';
  // Picking the stored model again drops the pick, so a later refetch cannot revive it as a change.
  const pick = (process: AiProcess, modelKey: string) =>
    setPicks((all) => {
      const { [process.key]: _dropped, ...rest } = all;
      return modelKey === process.model_key ? rest : { ...rest, [process.key]: modelKey };
    });
  const reset = () => {
    setPicks({});
    setFailed({});
  };

  const save = async () => {
    setSaving(true);
    const lost: Record<string, string> = {};
    let error: unknown = null;
    for (const process of dirty) {
      try {
        await assign.mutateAsync({ key: process.key, model_key: picks[process.key]! });
        setPicks((all) => Object.fromEntries(Object.entries(all).filter(([key]) => key !== process.key)));
      } catch (e) {
        lost[process.key] = e instanceof Error ? e.message : String(e);
        error = e;
      }
    }
    setFailed(lost);
    setOutcome(error ? { error } : { error: null, at: Date.now() });
    // One message for the whole batch; failed rows are listed in the section itself.
    if (!error) toast.success(dirty.length === 1 ? 'Model assigned' : 'Assignments saved');
    setSaving(false);
  };

  const state: ButtonState = { pending: saving, error: outcome.error, succeededAt: outcome.at };
  return { valueOf, pick, reset, save, changed: dirty.length > 0, saving, failures, state };
}
